
import {Component, Prop, Vue} from 'vue-property-decorator';
import DataloggerUsbFlash from "@/components/meters/detail/DataloggerUsbFlash.vue";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  DataloggerFirmware,
  DataloggerOtaRequest,
  DataloggerOverview,
  DataloggerRequestStatus,
  DataloggerStatus,
  DataloggerWifiRequest,
} from "@/data/models/datalogger";

dayjs.extend(relativeTime);

/** Libellés des statuts de demande (Wi-Fi et OTA) */
const STATUS_LABELS: { [status in DataloggerRequestStatus]: { text: string; color: string } } = {
  pending: {text: 'En attente', color: 'orange'},
  installing: {text: 'Installation', color: 'blue'},
  applied: {text: 'Appliquée', color: 'success'},
  failed: {text: 'Échec', color: 'error'},
  replaced: {text: 'Remplacée', color: 'grey'},
  cancelled: {text: 'Annulée', color: 'grey'},
};

/** Causes d'échec remontées par le boîtier */
const ERROR_LABELS: { [error: string]: string } = {
  no_ap: 'Réseau introuvable',
  auth: 'Mot de passe refusé',
  timeout: 'Délai dépassé',
  invalid: 'Commande invalide',
  publish: 'Envoi au broker impossible',
  url: 'URL refusée',
  size: 'Taille incorrecte',
  no_space: 'Place insuffisante',
  download: 'Téléchargement interrompu',
  md5: 'Somme de contrôle incorrecte',
  write: 'Écriture impossible',
  rollback: 'Nouvelle version rejetée au démarrage',
  no_connection: 'Nouvelle version sans connexion au broker',
};

/** Rejet de la nouvelle version : « rollback:<cause>[:<étape>] » (firmware 2026-10-19-1 et suivants) */
const ROLLBACK_CAUSES: { [cause: string]: string } = {
  not_started: 'jamais démarrée (image refusée)',
  panic: 'plantage',
  watchdog: 'blocage (watchdog)',
  brownout: 'alimentation insuffisante',
  power: 'coupure de courant',
  reset: 'bouton reset',
  restart: 'redémarrage logiciel',
  unknown: 'cause inconnue',
};

const ROLLBACK_STAGES: { [stage: string]: string } = {
  boot: 'avant la connexion réseau',
  network: 'réseau connecté, avant le broker',
  boot_loop: 'redémarrages en boucle',
};

/** Rafraîchissement tant qu'une demande est en cours */
const POLLING_INTERVAL = 5000;

@Component({components: {DataloggerUsbFlash}})
export default class DataloggerRemote extends Vue {
  @Prop({required: true})
  meterId: string;

  overview: DataloggerOverview = null;
  firmwares: Array<DataloggerFirmware> = [];
  loading: boolean = true;
  notFound: boolean = false;
  pollingTimer: number = null;

  // Wi-Fi
  wifiSsid: string = '';
  wifiPassword: string = '';
  showWifiPassword: boolean = false;
  sendingWifi: boolean = false;
  cancellingWifi: boolean = false;

  // OTA
  selectedFirmwareId: string = null;
  sendingOta: boolean = false;
  cancellingOta: boolean = false;


  wifiHeaders = [
    {text: 'Date', value: 'createdAt'},
    {text: 'SSID', value: 'ssid'},
    {text: 'Statut', value: 'status'},
    {text: 'Erreur', value: 'error'},
    {text: 'Signal', value: 'quality'},
  ];

  otaHeaders = [
    {text: 'Date', value: 'createdAt'},
    {text: 'Version', value: 'version'},
    {text: 'Depuis', value: 'fromVersion'},
    {text: 'Statut', value: 'status'},
    {text: 'Erreur', value: 'error'},
  ];

  ssidRules = [
    (v: string): boolean | string => !!v || 'SSID obligatoire',
    (v: string): boolean | string => new TextEncoder().encode(v || '').length <= 32 || '32 octets maximum',
  ];

  passwordRules = [
    (v: string): boolean | string => {
      const length = new TextEncoder().encode(v || '').length;
      return length === 0 || (length >= 8 && length <= 63) || 'Vide (réseau ouvert) ou 8 à 63 octets';
    },
  ];

  mounted(): void {
    this.load();
    this.loadFirmwares();
  }

  beforeDestroy(): void {
    this.stopPolling();
  }

  // ************************************
  // Chargement
  // ************************************

  async load(): Promise<void> {
    try {
      this.overview = await this.$dao.datalogger.getOverview(this.meterId);
      this.notFound = false;
    } catch (error) {
      if (error?.status === 404) {
        this.notFound = true;
      } else {
        this.showError(error, "Impossible de charger l'état du boîtier");
      }
    } finally {
      this.loading = false;
    }
    this.updatePolling();
  }

  async loadFirmwares(): Promise<void> {
    try {
      this.firmwares = await this.$dao.datalogger.getFirmwares();
    } catch (error) {
      this.showError(error, 'Impossible de charger la liste des firmwares');
    }
  }

  updatePolling(): void {
    if (this.pendingWifi || this.activeOta) {
      if (this.pollingTimer === null) {
        this.pollingTimer = window.setInterval(() => this.load(), POLLING_INTERVAL);
      }
    } else {
      this.stopPolling();
    }
  }

  stopPolling(): void {
    if (this.pollingTimer !== null) {
      window.clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  // ************************************
  // Affichage
  // ************************************

  get status(): DataloggerStatus | null {
    return this.overview?.status ?? null;
  }

  get pendingWifi(): DataloggerWifiRequest | null {
    return this.overview?.wifiRequests.find((request) => request.status === 'pending') ?? null;
  }

  get activeOta(): DataloggerOtaRequest | null {
    return this.overview?.otaRequests.find((request) => request.status === 'pending' || request.status === 'installing') ?? null;
  }

  get firmwareItems(): Array<{ value: string; text: string }> {
    return this.firmwares.map((firmware) => ({
      value: firmware.id,
      text: `${firmware.version} — ${firmware.filename || 'sans nom'} (${this.formatDate(firmware.createdAt)})`,
    }));
  }

  statusLabel(status: DataloggerRequestStatus): { text: string; color: string } {
    return STATUS_LABELS[status] || {text: status, color: 'grey'};
  }

  errorLabel(error: string | null): string {
    if (!error) {
      return '';
    }
    if (error.startsWith('http_')) {
      return `Erreur HTTP ${error.substring(5)}`;
    }
    if (error.startsWith('rollback:')) {
      const [, cause, stage] = error.split(':');
      const details = [ROLLBACK_CAUSES[cause] || cause, stage && (ROLLBACK_STAGES[stage] || stage)].filter(Boolean);
      return `${ERROR_LABELS.rollback} : ${details.join(', ')}`;
    }
    return ERROR_LABELS[error] || error;
  }

  fromNow(date: string): string {
    return dayjs(date).fromNow();
  }

  signalColor(quality: number): string {
    return quality >= 60 ? 'success' : quality >= 30 ? 'warning' : 'error';
  }

  formatDate(date: string | null): string {
    return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '';
  }


  showError(error: { json?: { error?: string } }, fallback: string): void {
    const message = error?.json?.error || fallback;
    this.$dialog.error({text: message, title: 'Erreur'});
  }

  // ************************************
  // Wi-Fi
  // ************************************

  sendWifi(): void {
    if (!(this.$refs.wifiForm as any).validate()) {
      return;
    }

    this.$dialog
        .confirm({
          actions: [{text: 'Annuler', key: false}, {text: 'Envoyer', key: true}],
          title: 'Configuration Wi-Fi',
          text: `Envoyer le réseau « ${this.wifiSsid} » au boîtier ${this.overview.mac} ? Il sera appliqué à sa prochaine connexion.`,
        })
        .then(async (confirmed) => {
          if (!confirmed) {
            return;
          }
          this.sendingWifi = true;
          try {
            await this.$dao.datalogger.requestWifi(this.meterId, this.wifiSsid, this.wifiPassword);
            this.wifiPassword = '';
            this.$dialog.notify.success('Configuration Wi-Fi envoyée');
            await this.load();
          } catch (error) {
            this.showError(error, "Impossible d'envoyer la configuration Wi-Fi");
          } finally {
            this.sendingWifi = false;
          }
        });
  }

  async cancelWifi(): Promise<void> {
    this.cancellingWifi = true;
    try {
      await this.$dao.datalogger.cancelWifi(this.meterId);
      await this.load();
    } catch (error) {
      this.showError(error, "Impossible d'annuler la demande Wi-Fi");
    } finally {
      this.cancellingWifi = false;
    }
  }

  // ************************************
  // OTA
  // ************************************

  sendOta(): void {
    const firmware = this.firmwares.find((item) => item.id === this.selectedFirmwareId);
    if (!firmware) {
      return;
    }

    this.$dialog
        .confirm({
          actions: [{text: 'Annuler', key: false}, {text: 'Mettre à jour', key: true}],
          title: 'Mise à jour du firmware',
          text: `Installer la version ${firmware.version} sur le boîtier ${this.overview.mac} (actuellement ${this.status?.fw || 'inconnue'}) ? `
              + 'Le boîtier redémarre après le téléchargement et revient seul à l\'ancienne version en cas de problème.',
        })
        .then(async (confirmed) => {
          if (!confirmed) {
            return;
          }
          this.sendingOta = true;
          try {
            await this.$dao.datalogger.requestOta(this.meterId, firmware.id);
            this.$dialog.notify.success('Mise à jour envoyée');
            await this.load();
          } catch (error) {
            this.showError(error, "Impossible d'envoyer la mise à jour");
          } finally {
            this.sendingOta = false;
          }
        });
  }

  async cancelOta(): Promise<void> {
    this.cancellingOta = true;
    try {
      await this.$dao.datalogger.cancelOta(this.meterId);
      await this.load();
    } catch (error) {
      this.showError(error, "Impossible d'annuler la mise à jour");
    } finally {
      this.cancellingOta = false;
    }
  }
}
